import { useCallback, useEffect, useState } from 'react';
import { getBranchReceipts } from '../services';
import { IBranchReceiptItem, IBranchReceiptsData } from '../types';
import { resolveErrorMessage } from './hookUtils';

interface BranchReceiptsListState {
	receipts: IBranchReceiptItem[];
	pagination: IBranchReceiptsData['pagination'] | null;
	totals: IBranchReceiptsData['totals'] | null;
	loading: boolean;
	loadingMore: boolean;
	error: string | null;
	cachedAt: string | null;
	loadMore: () => void;
	hasMore: boolean;
	refetch: () => void;
}

export function useBranchReceiptsList(branch: string | undefined, pageSize = 25): BranchReceiptsListState {
	const [receipts, setReceipts] = useState<IBranchReceiptItem[]>([]);
	const [pagination, setPagination] = useState<IBranchReceiptsData['pagination'] | null>(null);
	const [totals, setTotals] = useState<IBranchReceiptsData['totals'] | null>(null);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [cachedAt, setCachedAt] = useState<string | null>(null);
	const [offset, setOffset] = useState(0);
	const [tick, setTick] = useState(0);

	useEffect(() => {
		if (!branch) {
			setLoading(false);
			return;
		}

		let cancelled = false;
		const isFirstPage = offset === 0;
		if (isFirstPage) {
			setLoading(true);
			setError(null);
		} else {
			setLoadingMore(true);
		}

		getBranchReceipts({ branch, limit: pageSize, offset })
			.then(({ data, cachedAt: newCachedAt }) => {
				if (cancelled) return;
				setReceipts((prev) => (isFirstPage ? data.receipts : [...prev, ...data.receipts]));
				setPagination(data.pagination);
				setTotals(data.totals);
				setCachedAt(newCachedAt);
				setLoading(false);
				setLoadingMore(false);
			})
			.catch((err) => {
				if (cancelled) return;
				setError(resolveErrorMessage(err));
				setLoading(false);
				setLoadingMore(false);
			});

		return () => {
			cancelled = true;
		};
	}, [branch, pageSize, offset, tick]); // tick fuerza refetch manual

	useEffect(() => {
		setReceipts([]);
		setPagination(null);
		setTotals(null);
		setOffset(0);
	}, [branch]);

	const hasMore = pagination !== null && receipts.length < pagination.total;

	const loadMore = useCallback(() => {
		if (loading || loadingMore) return;
		setOffset((prev) => prev + pageSize);
	}, [loading, loadingMore, pageSize]);

	const refetch = useCallback(() => {
		setOffset(0);
		setTick((t) => t + 1);
	}, []);

	return { receipts, pagination, totals, loading, loadingMore, error, cachedAt, loadMore, hasMore, refetch };
}
